// Estado global de parlantes Sonos (análogo a mapaDispositivos en consumptionService)
import { io, Socket } from 'socket.io-client';
import { eventBus } from './EventBus';
import { getServerUrl } from '../config';
import {
  fetchSonosSpeakers,
  refreshActiveSonosSpeakers,
  refreshSonosSpeakersByIds,
  SonosSpeaker,
  isSonosPlaying,
  withPreferredAlbumArt,
  notifyQueueReordered,
} from './sonosApi';

export const mapaSonos: Record<number, SonosSpeaker> = {};

let mapaSonosCargado = false;
let playingPollInterval: ReturnType<typeof setInterval> | null = null;
let activeRefreshTimer: ReturnType<typeof setTimeout> | null = null;
let activeRefreshInFlight = false;
let sonosRefreshInFlight: Promise<SonosSpeaker[]> | null = null;
let sonosSocket: Socket | null = null;

const PLAYING_POLL_MS = 4000;
const ACTIVE_REFRESH_DEBOUNCE_MS = 350;

export function isMapaSonosCargado(): boolean {
  return mapaSonosCargado;
}

export function getSonosByHabitacion(habitacionId: number): SonosSpeaker[] {
  const roomId = Number(habitacionId);
  return Object.values(mapaSonos).filter(
    (s) => Number(s.habitacion_id) === roomId && s.enabled !== false,
  );
}

function anySonosPlaying(): boolean {
  return Object.values(mapaSonos).some((s) => isSonosPlaying(s.playback_state));
}

function syncPlayingPoll(): void {
  if (anySonosPlaying() && !playingPollInterval) {
    playingPollInterval = setInterval(() => {
      void refrescarSonosActivos();
    }, PLAYING_POLL_MS);
  } else if (!anySonosPlaying() && playingPollInterval) {
    clearInterval(playingPollInterval);
    playingPollInterval = null;
  }
}

function ensureSonosSocket(): void {
  if (sonosSocket) return;
  const token = localStorage.getItem('token');
  if (!token) return;

  let serverUrl = getServerUrl();
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    serverUrl = serverUrl.replace(/^http:/, 'https:').replace(/^ws:/, 'wss:');
  }

  sonosSocket = io(serverUrl, {
    path: '/socket.io-sonos',
    auth: { token },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: 10,
    reconnectionDelay: 1000,
  });

  sonosSocket.on('sonos_status', (data: Partial<SonosSpeaker> & { id?: number }) => {
    if (!data?.id) return;
    const id = Number(data.id);
    const existing = mapaSonos[id];
    if (!existing) {
      void refrescarSonos();
      return;
    }
    mergeSonosSpeakers([{ ...existing, ...data, id } as SonosSpeaker]);
  });

  // El backend re-mezcló la cola en segundo plano (aleatorio SHC tras reemplazar
  // o ampliar la cola): recargar la cola visible.
  sonosSocket.on('sonos_queue_changed', (data: { id?: number; ids?: number[] }) => {
    const ids = (Array.isArray(data?.ids) && data.ids.length ? data.ids : [data?.id])
      .map((v) => Number(v))
      .filter((v) => Number.isFinite(v) && v > 0);
    if (ids.length) notifyQueueReordered(ids);
  });
}

export async function refrescarSonosActivos(): Promise<SonosSpeaker[]> {
  if (activeRefreshInFlight) return Object.values(mapaSonos);
  activeRefreshInFlight = true;
  try {
    const speakers = await refreshActiveSonosSpeakers();
    if (speakers.length) mergeSonosSpeakers(speakers);
    return Object.values(mapaSonos);
  } catch {
    return refrescarSonos();
  } finally {
    activeRefreshInFlight = false;
  }
}

export function scheduleSonosActiveRefresh(): void {
  if (activeRefreshTimer) clearTimeout(activeRefreshTimer);
  activeRefreshTimer = setTimeout(() => {
    activeRefreshTimer = null;
    void refrescarSonosActivos();
  }, ACTIVE_REFRESH_DEBOUNCE_MS);
}

async function cargarSonosNow(): Promise<SonosSpeaker[]> {
  try {
    const speakers = await fetchSonosSpeakers();
    if (!Array.isArray(speakers)) return Object.values(mapaSonos);
    if (speakers.length === 0 && Object.keys(mapaSonos).length > 0) {
      mapaSonosCargado = true;
      return Object.values(mapaSonos);
    }
    speakers.forEach((s) => {
      const id = Number(s.id);
      const prev = mapaSonos[id];
      const next = withPreferredAlbumArt({ ...(prev || {}), ...s, id });
      if (s.habitacion_id === undefined && prev?.habitacion_id != null) {
        next.habitacion_id = prev.habitacion_id;
      }
      if (s.enabled == null && prev) next.enabled = prev.enabled;
      mapaSonos[id] = next;
    });
    mapaSonosCargado = true;
    syncPlayingPoll();
    ensureSonosSocket();
    eventBus.emit('sonos:updated', { speakers: Object.values(mapaSonos) });
    return Object.values(mapaSonos);
  } catch {
    mapaSonosCargado = true;
    return Object.values(mapaSonos);
  }
}

export function cargarSonos(): Promise<SonosSpeaker[]> {
  if (sonosRefreshInFlight) return sonosRefreshInFlight;
  sonosRefreshInFlight = cargarSonosNow().finally(() => {
    sonosRefreshInFlight = null;
  });
  return sonosRefreshInFlight;
}

export async function refrescarSonos(): Promise<SonosSpeaker[]> {
  return cargarSonos();
}

/** Sync LAN de parlantes concretos (habitación visible) sin barrer toda la casa. */
export async function refrescarSonosPorIds(ids: number[]): Promise<SonosSpeaker[]> {
  if (!ids.length) return [];
  try {
    const speakers = await refreshSonosSpeakersByIds(ids);
    if (speakers.length) mergeSonosSpeakers(speakers);
    return speakers;
  } catch {
    return Object.values(mapaSonos).filter((s) => ids.includes(s.id));
  }
}

/** Fusiona parlantes actualizados tras control/play sin esperar al poll. */
export function mergeSonosSpeakers(updated: SonosSpeaker[]): void {
  updated.forEach((s) => {
    const id = Number(s.id);
    const prev = mapaSonos[id];
    const merged = { ...(prev || {}), ...s, id } as SonosSpeaker;
    if ((s.habitacion_id === undefined || s.habitacion_id === null) && prev?.habitacion_id != null) {
      merged.habitacion_id = prev.habitacion_id;
    }
    if ((s.enabled === undefined || s.enabled === null) && prev) merged.enabled = prev.enabled;
    if (s.enabled === false && prev && prev.enabled !== false && s.nombre == null) {
      merged.enabled = prev.enabled;
    }
    // Tapa ya vista en biblioteca antes que la del parlante (evita ir afuera).
    mapaSonos[id] = withPreferredAlbumArt(merged);
  });
  syncPlayingPoll();
  eventBus.emit('sonos:updated', { speakers: Object.values(mapaSonos) });
}

eventBus.on('estructura:refrescar', () => {
  void refrescarSonos();
});
